/**
 * SampleHandSimulator - Interactive sample hand drawing tool
 *
 * Draws opening hands from the main deck, evaluates mana, colors and early
 * plays through the shared hand-sim engine, runs a real London mulligan with an
 * explicit bottoming step, and steps a kept hand forward turn by turn.
 */
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import type { KeyboardEvent, ReactElement } from 'react';
import { useMemo, useRef, useState } from 'react';
import { IoArrowUndo, IoCheckmarkCircle, IoShuffle } from 'react-icons/io5';

import InfoButton from '@/components/InfoButton/InfoButton';
import { useTurnSimulation } from '@/features/decks/hooks/useTurnSimulation';
import type { KeepRules, SimCard, TurnSimOptions } from '@/features/decks/lib/handSim';
import { buildLibrary, buildZone, drawHand, earliestCastTurn, evaluateHand, sortHand } from '@/features/decks/lib/handSim';
import { openDeckCardDetailsAtom } from '@/features/decks/state/deckCardDetailsAtom';

import type { EnrichedDeckCard } from '../../types';

import HandCardTile from './HandCardTile';
import type { HandHistoryEntry } from './HandHistoryStrip';
import HandHistoryStrip, { HAND_HISTORY_LIMIT } from './HandHistoryStrip';
import HandVerdictBar from './HandVerdictBar';
import SampleHandInfo from './SampleHandInfo';
import TurnSimulationPanel from './TurnSimulationPanel';

// ============================================================================
// Types
// ============================================================================

export interface SampleHandSimulatorProps {
  cards: EnrichedDeckCard[];
  rules: KeepRules;
  turnOptions: TurnSimOptions;
  className?: string;
}

type Phase = 'idle' | 'deciding' | 'bottoming' | 'kept';

const OPENING_HAND_SIZE = 7;
/** London mulligans stop here — a hand below 4 cards is not worth simulating. */
const MIN_HAND_SIZE = 4;

// ============================================================================
// Component
// ============================================================================

export default function SampleHandSimulator({ cards, rules, turnOptions, className }: SampleHandSimulatorProps): ReactElement {
  const openCardDetails = useSetAtom(openDeckCardDetailsAtom);

  const [phase, setPhase] = useState<Phase>('idle');
  const [hand, setHand] = useState<SimCard[]>([]);
  const [mulliganCount, setMulliganCount] = useState(0);
  const [bottomKeys, setBottomKeys] = useState<string[]>([]);
  const [history, setHistory] = useState<HandHistoryEntry[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const nextIdRef = useRef(1);

  const library = useMemo(() => buildLibrary(cards), [cards]);
  const commanders = useMemo(() => buildZone(cards, 'commander'), [cards]);

  const isBottoming = phase === 'bottoming';
  const keptHand = isBottoming ? hand.filter((card) => !bottomKeys.includes(card.key)) : hand;
  const evaluation = evaluateHand(keptHand, rules);

  const turnSim = useTurnSimulation(keptHand, library, commanders, turnOptions);

  const turnByKey = new Map<string, number | null>();
  keptHand.forEach((card, index) => turnByKey.set(card.key, evaluation.castableTurn[index]));

  const canMulligan = OPENING_HAND_SIZE - mulliganCount > MIN_HAND_SIZE;
  const bottomsRemaining = mulliganCount - bottomKeys.length;
  const primaryIsKeep = phase === 'deciding' && mulliganCount > 0;

  // ── Actions ──

  const record = (outcome: HandHistoryEntry['outcome'], entryHand: SimCard[], mulligans: number): void => {
    const entry: HandHistoryEntry = { id: nextIdRef.current++, hand: entryHand, mulliganCount: mulligans, outcome };
    setHistory((prev) => [...prev, entry].slice(-HAND_HISTORY_LIMIT));
    setActiveId(entry.id);
  };

  const deal = (mulligans: number): void => {
    setHand(drawHand(library, OPENING_HAND_SIZE));
    setMulliganCount(mulligans);
    setBottomKeys([]);
    setPhase('deciding');
    setActiveId(null);
    turnSim.reset();
  };

  const handleNewHand = (): void => {
    if (phase === 'deciding' || phase === 'bottoming') record('mulliganed', hand, mulliganCount);
    deal(0);
  };

  const handleMulligan = (): void => {
    if (phase !== 'deciding' || !canMulligan) return;
    record('mulliganed', hand, mulliganCount);
    deal(mulliganCount + 1);
  };

  const handleKeep = (): void => {
    if (phase !== 'deciding') return;
    turnSim.reset();
    if (mulliganCount === 0) {
      record('kept', hand, 0);
      setPhase('kept');
      return;
    }
    setPhase('bottoming');
  };

  const handleConfirmBottom = (): void => {
    if (bottomsRemaining !== 0) return;
    record('kept', keptHand, mulliganCount);
    setHand(keptHand);
    setBottomKeys([]);
    setPhase('kept');
    turnSim.reset();
  };

  const handleUndo = (): void => {
    const previous = history.at(-1);
    if (!previous) return;
    setHistory((prev) => prev.slice(0, -1));
    setHand(previous.hand);
    setMulliganCount(previous.mulliganCount);
    setBottomKeys([]);
    setPhase('deciding');
    setActiveId(null);
    turnSim.reset();
  };

  const handleRestore = (entry: HandHistoryEntry): void => {
    setHand(entry.hand);
    setMulliganCount(entry.mulliganCount);
    setBottomKeys([]);
    setPhase(entry.outcome === 'kept' ? 'kept' : 'deciding');
    setActiveId(entry.id);
    turnSim.reset();
  };

  const toggleBottom = (key: string): void => {
    setBottomKeys((prev) => {
      if (prev.includes(key)) return prev.filter((existing) => existing !== key);
      return prev.length >= mulliganCount ? prev : [...prev, key];
    });
  };

  const openDetails = (card: SimCard): void => {
    openCardDetails({ scryfallId: card.scryfallId, deckCardId: card.deckCardId });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    switch (event.key.toLowerCase()) {
      case 'n':
        handleNewHand();
        break;
      case 'm':
        handleMulligan();
        break;
      case 'k':
        handleKeep();
        break;
      case 'u':
        handleUndo();
        break;
      case 't':
        if (phase !== 'kept' || turnSim.atLimit) return;
        turnSim.next();
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  // ── Empty state ──

  if (library.length < OPENING_HAND_SIZE) {
    return (
      <div className={clsx('space-y-3', className)}>
        <SimulatorHeading />
        <div className="rounded-lg bg-zinc-800/30 p-4 text-center">
          <p className="text-ash text-sm">Need at least 7 main-deck cards to draw a hand</p>
          <p className="text-ash/60 mt-1 text-xs">Commanders, companions and sideboard cards are never shuffled into the library</p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-3', className)} tabIndex={-1} onKeyDown={handleKeyDown}>
      <div className="flex items-center justify-between">
        <SimulatorHeading />
        {mulliganCount > 0 && <span className="text-xs text-amber-400/80">Mulligan to {OPENING_HAND_SIZE - mulliganCount}</span>}
      </div>

      {phase === 'idle' ? (
        <div className="rounded-lg bg-zinc-800/30 p-6 text-center">
          <p className="text-silver mb-3 text-sm">Draw a sample opening hand</p>
          <button
            type="button"
            onClick={() => deal(0)}
            className="bg-aether hover:bg-aether/80 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
          >
            <IoShuffle className="h-4 w-4" />
            Draw 7 Cards
          </button>
        </div>
      ) : (
        <>
          {commanders.length > 0 && (
            <div className="space-y-1">
              <span className="text-ash text-[10px] tracking-wider uppercase">Command zone</span>
              <div className="flex flex-wrap gap-1.5">
                {commanders.map((commander) => {
                  const turn = earliestCastTurn(commander, keptHand);
                  return (
                    <div key={commander.key} className="w-[calc(25%-0.375rem)]">
                      <HandCardTile
                        card={commander}
                        castableTurn={turn}
                        selectable={false}
                        selected={false}
                        onToggleSelect={toggleBottom}
                        onOpenDetails={openDetails}
                        annotation={turn !== null ? `castable T${turn}` : 'not castable'}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-1.5">
            {sortHand(hand).map((card) => (
              <div key={card.key} className="w-[calc(25%-0.375rem)]">
                <HandCardTile
                  card={card}
                  castableTurn={turnByKey.get(card.key) ?? null}
                  selectable={isBottoming}
                  selected={bottomKeys.includes(card.key)}
                  disabled={isBottoming && bottomsRemaining === 0 && !bottomKeys.includes(card.key)}
                  onToggleSelect={toggleBottom}
                  onOpenDetails={openDetails}
                />
              </div>
            ))}
          </div>

          <div className="text-ash/70 flex flex-wrap items-center justify-center gap-3 text-[10px]">
            <LegendSwatch className="bg-emerald-500/60" label="land" />
            <LegendSwatch className="bg-amber-400/60" label="mana rock" />
            <LegendSwatch className="bg-zinc-600" label="dimmed = uncastable" />
          </div>

          {isBottoming && (
            <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-300" role="status">
              {bottomsRemaining > 0
                ? `Pick ${bottomsRemaining} more card${bottomsRemaining === 1 ? '' : 's'} to put on the bottom`
                : 'Ready — confirm to keep this hand'}
            </p>
          )}

          <HandVerdictBar evaluation={evaluation} />

          {phase === 'kept' && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-400">
              <IoCheckmarkCircle className="h-4 w-4" />
              Kept at {keptHand.length} cards
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {isBottoming ? (
              <button
                type="button"
                onClick={handleConfirmBottom}
                disabled={bottomsRemaining !== 0}
                className="bg-aether hover:bg-aether/80 flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500"
              >
                Confirm bottom
              </button>
            ) : (
              phase === 'deciding' && (
                <>
                  <button
                    type="button"
                    onClick={handleKeep}
                    className={clsx(
                      'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
                      primaryIsKeep
                        ? 'bg-aether hover:bg-aether/80 text-white'
                        : 'border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10',
                    )}
                  >
                    Keep
                  </button>
                  <button
                    type="button"
                    onClick={handleMulligan}
                    disabled={!canMulligan}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-amber-500/40 px-3 py-2 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/10 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:border-zinc-700 disabled:text-zinc-600"
                  >
                    Mulligan to {OPENING_HAND_SIZE - mulliganCount - 1}
                  </button>
                </>
              )
            )}

            <button
              type="button"
              onClick={handleNewHand}
              className={clsx(
                'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
                // Once a hand is kept, stepping turns is the primary action.
                primaryIsKeep || isBottoming || phase === 'kept'
                  ? 'border border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                  : 'bg-aether hover:bg-aether/80 text-white',
              )}
            >
              <IoShuffle className="h-4 w-4" />
              New Hand
            </button>

            <button
              type="button"
              onClick={handleUndo}
              disabled={history.length === 0}
              className="flex items-center justify-center rounded-lg px-3 py-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:text-zinc-700"
              aria-label="Undo to the previous hand"
              title={history.length === 0 ? 'No previous hand yet' : 'Undo to the previous hand'}
            >
              <IoArrowUndo className="h-4 w-4" />
            </button>
          </div>

          {!canMulligan && phase === 'deciding' && (
            <p className="text-ash/70 text-[11px]">Mulligan floor reached — hands below {MIN_HAND_SIZE} cards are not simulated.</p>
          )}

          {phase === 'kept' && <TurnSimulationPanel hand={keptHand} options={turnOptions} state={turnSim} onOpenDetails={openDetails} />}

          <HandHistoryStrip entries={history} activeId={activeId} onSelect={handleRestore} />
        </>
      )}
    </div>
  );
}

// ============================================================================
// Local presentation helpers
// ============================================================================

function SimulatorHeading(): ReactElement {
  return (
    <div className="flex items-center gap-1">
      <span className="text-ash text-xs font-medium">Sample Hand</span>
      <InfoButton title="Sample Hand Simulator">
        <SampleHandInfo />
      </InfoButton>
    </div>
  );
}

function LegendSwatch({ className, label }: { className: string; label: string }): ReactElement {
  return (
    <span className="flex items-center gap-1">
      <span className={clsx('h-2 w-2 rounded-full', className)} />
      {label}
    </span>
  );
}
